import { OPERATORS, effectiveScale, isChoice, loopSource, operatorLabel, subItems, type Condition, type Definition, type Operator, type Question } from '@survey/core';
import { ChevronDown, ChevronRight, Flag, GitBranch, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useI18n } from '../../i18n';

interface Props {
  def: Definition;
  question: Question;
  onChange: (q: Question) => void;
}

const NUMERIC: Operator[] = ['equals', 'not_equals', 'gt', 'gte', 'lt', 'lte', 'answered', 'not_answered'];
const TEXT: Operator[] = ['equals', 'not_equals', 'contains', 'not_contains', 'answered', 'not_answered'];
const PRESENCE: Operator[] = ['answered', 'not_answered'];

/** Operators that make sense for a source question (or one of its rows). */
export function operatorsFor(src: Question, row?: string): Operator[] {
  if (row) {
    if (src.type === 'constant_sum') return NUMERIC;
    if (src.type === 'text_form') return TEXT;
    return src.matrixMultiple ? ['contains', 'not_contains', 'answered', 'not_answered'] : ['equals', 'not_equals', 'answered', 'not_answered'];
  }
  switch (src.type) {
    case 'number':
    case 'rating':
    case 'date':
    case 'slider':
    case 'nps':
      return NUMERIC;
    case 'multiple_choice':
      return ['contains', 'not_contains', 'answered', 'not_answered'];
    case 'short_text':
    case 'long_text':
      return TEXT;
    case 'matrix':
    case 'constant_sum':
    case 'text_form':
    case 'file_upload':
    case 'signature':
    case 'location':
      return PRESENCE;
    default:
      return ['equals', 'not_equals', 'answered', 'not_answered'];
  }
}

export function defaultValue(src: Question, row?: string): Condition['value'] {
  if (row) {
    if (src.type === 'matrix') return src.options?.[0]?.id ?? '';
    if (src.type === 'constant_sum') return 0;
    return '';
  }
  if (isChoice(src.type) || src.type === 'ranking') return src.options?.[0]?.id ?? '';
  if (src.type === 'yes_no') return true;
  if (src.type === 'rating' || src.type === 'nps') return effectiveScale(src).max;
  if (src.type === 'number' || src.type === 'slider') return 0;
  return '';
}

export function ValueInput({ src, row, value, onChange }: { src: Question; row?: string; value: Condition['value']; onChange: (v: Condition['value']) => void }) {
  const { t } = useI18n();
  const choices = (row && src.type === 'matrix') || ((isChoice(src.type) || src.type === 'ranking') && !row);
  if (choices) {
    return (
      <select className="select" value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} aria-label={t('logic.value')}>
        {src.carryForward && <option value="">{t('logic.carried_hint')}</option>}
        {src.options?.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }
  if (src.type === 'yes_no') {
    return (
      <select className="select" value={String(value)} onChange={(e) => onChange(e.target.value === 'true')} aria-label={t('logic.value')}>
        <option value="true">{t('common.yes')}</option>
        <option value="false">{t('common.no')}</option>
      </select>
    );
  }
  if (src.type === 'rating' || src.type === 'nps') {
    const s = effectiveScale(src);
    return (
      <select className="select" value={String(value)} onChange={(e) => onChange(Number(e.target.value))} aria-label={t('logic.value')}>
        {Array.from({ length: s.max - s.min + 1 }, (_, i) => s.min + i).map((n) => (
          <option key={n} value={n}>
            {n}
          </option>
        ))}
      </select>
    );
  }
  if (src.type === 'number' || src.type === 'slider' || (row && src.type === 'constant_sum')) {
    return <input className="input" type="number" value={typeof value === 'number' ? value : ''} onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))} aria-label={t('logic.value')} />;
  }
  if (src.type === 'date') {
    return <input className="input" type="date" value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} aria-label={t('logic.value')} />;
  }
  return <input className="input" value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} placeholder={t('logic.text_placeholder')} aria-label={t('logic.value')} />;
}

/** Questions a condition can read: answered types, outside other loops. */
export function conditionSources(def: Definition, candidates: Question[], from?: Question): Question[] {
  const myLoop = from && loopSource(def, from) ? from.sectionTitle?.trim() : undefined;
  return candidates.filter((q) => {
    if (q.type === 'descriptive') return false;
    const loop = loopSource(def, q) ? q.sectionTitle?.trim() : undefined;
    return !loop || loop === myLoop;
  });
}

/** One "question [row] operator value" condition. */
export function ConditionRow({ def, sources, condition: c, onChange, onRemove, invalid }: { def: Definition; sources: Question[]; condition: Condition; onChange: (c: Condition) => void; onRemove: () => void; invalid?: boolean }) {
  const { t, locale } = useI18n();
  const src = def.questions.find((q) => q.id === c.questionId);
  const rows = src ? subItems(src) : [];
  const ops = src ? operatorsFor(src, c.row) : OPERATORS.map((o) => o.op);
  const needsValue = OPERATORS.find((o) => o.op === c.operator)?.needsValue ?? true;
  const bad = invalid || !src || !sources.includes(src);
  return (
    <div className="logic-row">
      <GitBranch size={16} className="muted" />
      <select
        className={`select ${bad ? 'invalid' : ''}`}
        value={c.questionId}
        aria-label={t('logic.question')}
        onChange={(e) => {
          const s = def.questions.find((q) => q.id === e.target.value)!;
          onChange({ questionId: s.id, operator: operatorsFor(s)[0]!, value: defaultValue(s) });
        }}
      >
        {bad && <option value={c.questionId}>{t('logic.missing_source')}</option>}
        {sources.map((q) => (
          <option key={q.id} value={q.id}>
            {def.questions.indexOf(q) + 1}. {q.title || t('common.untitled_question')}
          </option>
        ))}
      </select>
      {src && rows.length > 0 && (
        <select
          className="select"
          value={c.row ?? ''}
          aria-label={t('logic.row')}
          onChange={(e) => {
            const row = e.target.value || undefined;
            onChange({ questionId: c.questionId, row, operator: operatorsFor(src, row)[0]!, value: defaultValue(src, row) });
          }}
        >
          <option value="">{t('logic.whole_question')}</option>
          {rows.map((r) => (
            <option key={r.id} value={r.id}>
              {r.label}
            </option>
          ))}
        </select>
      )}
      <select className="select" value={c.operator} aria-label={t('logic.operator')} onChange={(e) => onChange({ ...c, operator: e.target.value as Operator })}>
        {ops.map((op) => (
          <option key={op} value={op}>
            {operatorLabel(op, locale)}
          </option>
        ))}
      </select>
      {needsValue && src ? <ValueInput src={src} row={c.row} value={c.value} onChange={(v) => onChange({ ...c, value: v })} /> : <span />}
      <button type="button" className="btn ghost sm icon" onClick={onRemove} aria-label={t('logic.remove')}>
        <Trash2 />
      </button>
    </div>
  );
}


/** The answers a question can be routed on, one per possible pick: choice options (also carried ones), yes/no, or scale points. */
export function routeAnswers(def: Definition, q: Question, yes: string, no: string): { value: string | number | boolean; label: string }[] {
  if (isChoice(q.type)) {
    const src = q.carryForward ? def.questions.find((x) => x.id === q.carryForward!.questionId) : undefined;
    return ((src ?? q).options ?? []).map((o, i) => ({ value: o.id, label: o.label || `#${i + 1}` }));
  }
  if (q.type === 'yes_no') return [{ value: true, label: yes }, { value: false, label: no }];
  if (q.type === 'rating' || q.type === 'nps') {
    const { min, max } = effectiveScale(q);
    return max - min <= 20 ? Array.from({ length: max - min + 1 }, (_, i) => ({ value: min + i, label: String(min + i) })) : [];
  }
  return [];
}

/** Long titles would stretch a select past its panel; the option shows the start of it. */
const short = (text: string, max = 60) => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

/** Sends each answer of a question to its own next question (or an ending). */
function AnswerRouting({ def, question, onChange }: Props) {
  const { t } = useI18n();
  // Open by default: this is where an answer is sent to a question, and closed it looked missing (UAT TC-02).
  const [open, setOpen] = useState(true);
  const idx = def.questions.findIndex((q) => q.id === question.id);
  const endings = def.endings ?? [];
  const op: Operator = question.type === 'multiple_choice' ? 'contains' : 'equals';
  const rules = question.jumpRules ?? [];
  const answers = routeAnswers(def, question, t('common.yes'), t('common.no'));
  type Value = (typeof answers)[number]['value'];
  const isRoute = (r: NonNullable<Question['jumpRules']>[number], value: Value) => r.operator === op && r.value === value;
  const targetOf = (optionId: Value) => {
    const r = rules.find((x) => isRoute(x, optionId));
    return r ? (r.targetEndingId ? `end:${r.targetEndingId}` : r.targetQuestionId) : '';
  };
  function route(optionId: Value, v: string) {
    const rest = rules.filter((x) => !isRoute(x, optionId));
    const next = v ? [...rest, { operator: op, value: optionId, targetQuestionId: v.startsWith('end:') ? '' : v, targetEndingId: v.startsWith('end:') ? v.slice(4) : undefined }] : rest;
    // Keep the rules in the order of the options so the list reads like the form.
    const order = answers.map((a) => a.value);
    next.sort((a, b) => order.indexOf(a.value as Value) - order.indexOf(b.value as Value));
    onChange({ ...question, jumpRules: next.length ? next : undefined });
  }
  return (
    <div className="stack gap-8 answer-routing">
      <button type="button" className={`route-toggle${open ? ' open' : ''}`} aria-expanded={open} onClick={() => setOpen(!open)}>
        <GitBranch className="route-icon" />
        <span className="grow">
          <strong className="small">{t('logic.route_title')}</strong>
          <span className="route-sub">{t('logic.route_sub')}</span>
        </span>
        {rules.length > 0 && <span className="pill blue">{t('logic.route_count', { count: rules.length })}</span>}
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>
      {!open && rules.length > 0 && (
        <ul className="route-summary">
          {answers
            .filter((a) => targetOf(a.value))
            .map((a) => {
              const target = targetOf(a.value);
              const ending = target.startsWith('end:') ? endings.find((e) => `end:${e.id}` === target) : undefined;
              const n = def.questions.findIndex((q) => q.id === target) + 1;
              return (
                <li key={String(a.value)}>
                  <span className="route-from">{a.label}</span> → <strong>{ending ? t('logic.end_with', { name: ending.name || ending.id }) : t('flow.q_ref', { n })}</strong>
                </li>
              );
            })}
        </ul>
      )}
      {open && <p className="hint">{t('logic.route_hint')}</p>}
      {open && answers.map((o) => (
        <div key={String(o.value)} className="answer-route">
          <span className="small answer-route-label" title={o.label}>{o.label}</span>
          <select className="select answer-route-select" value={targetOf(o.value)} aria-label={t('logic.route_for', { name: o.label })} onChange={(e) => route(o.value, e.target.value)}>
            <option value="">{t('logic.route_next')}</option>
            {idx < def.questions.length - 1 && (
              <optgroup label={t('logic.jump_questions')}>
                {def.questions.slice(idx + 1).map((target, n) => (
                  <option key={target.id} value={target.id}>
                    {idx + n + 2}. {short(target.title || t('common.untitled_question'))}
                  </option>
                ))}
              </optgroup>
            )}
            {endings.length > 0 && (
              <optgroup label={t('logic.jump_endings')}>
                {endings.map((e) => (
                  <option key={e.id} value={`end:${e.id}`}>
                    {t('logic.end_with', { name: e.name || e.id })}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>
      ))}
    </div>
  );
}

/** Basic show/skip logic (FR-05): show this question only when conditions on earlier answers hold. */
export function LogicEditor({ def, question, onChange }: Props) {
  const { t, locale } = useI18n();
  const idx = def.questions.findIndex((q) => q.id === question.id);
  const earlier = conditionSources(def, def.questions.slice(0, idx), question);
  const vi = question.visibleIf;
  const conditions = vi?.conditions ?? [];
  const jump = question.jumpIf;
  const endings = def.endings ?? [];
  const routable = routeAnswers(def, question, '', '').length > 0;
  const canJump = question.type !== 'descriptive' && (idx < def.questions.length - 1 || endings.length > 0);

  function setConditions(next: Condition[], match = vi?.match ?? 'all') {
    onChange({ ...question, visibleIf: next.length ? { match, conditions: next } : undefined });
  }

  function addCondition() {
    const src = earlier[earlier.length - 1];
    if (!src) return;
    setConditions([...conditions, { questionId: src.id, operator: operatorsFor(src)[0]!, value: defaultValue(src) }]);
  }

  const jumpTarget = jump ? (jump.targetEndingId ? `end:${jump.targetEndingId}` : jump.targetQuestionId) : '';
  const firstTarget = def.questions[idx + 1]?.id;

  return (
    <div className="stack gap-12">
      {earlier.length === 0 ? (
        <p className="small muted">{t('logic.first_always')}</p>
      ) : (
        <>
          {conditions.length === 0 ? (
            <p className="small muted">{t('logic.always_shown')}</p>
          ) : (
            <div className="row gap-8 wrap small">
              {t('logic.show_when')}
              <select className="select" style={{ width: 'auto', height: 30 }} value={vi?.match ?? 'all'} onChange={(e) => setConditions(conditions, e.target.value as 'all' | 'any')} aria-label={t('logic.match')}>
                <option value="all">{t('logic.all')}</option>
                <option value="any">{t('logic.any')}</option>
              </select>
              {t('logic.of_these')}
            </div>
          )}
          {conditions.map((c, i) => (
            <ConditionRow
              key={i}
              def={def}
              sources={earlier}
              condition={c}
              invalid={def.questions.findIndex((q) => q.id === c.questionId) >= idx}
              onChange={(next) => setConditions(conditions.map((x, j) => (j === i ? next : x)))}
              onRemove={() => setConditions(conditions.filter((_, j) => j !== i))}
            />
          ))}
          <div>
            <button type="button" className="btn sm" onClick={addCondition}>
              <Plus /> {t('logic.add')}
            </button>
          </div>
        </>
      )}
      {canJump && routable && (
        <>
          <div className="divider" />
          <AnswerRouting def={def} question={question} onChange={onChange} />
        </>
      )}
      {canJump && (
        <>
          <div className="divider" />
          <div className="stack gap-8">
            <strong className="small">{t('logic.jump_title')}</strong>
            <div className="row gap-8 wrap">
              <span className="small">{t('logic.jump_when')}</span>
              <select
                className="select"
                value={jump?.operator ?? ''}
                aria-label={t('logic.jump_operator')}
                onChange={(e) => {
                  const op = e.target.value as Operator;
                  onChange({
                    ...question,
                    jumpIf: op
                      ? { operator: op, value: jump?.value ?? defaultValue(question), targetQuestionId: jump?.targetQuestionId ?? (jump?.targetEndingId ? '' : (firstTarget ?? '')), targetEndingId: jump?.targetEndingId ?? (firstTarget ? undefined : endings[0]?.id) }
                      : undefined,
                  });
                }}
              >
                <option value="">{t('logic.jump_disabled')}</option>
                {operatorsFor(question)
                  // "is" per answer is what "Send each answer to a question" does, so it is not offered again here.
                  .filter((op) => !(routable && op === (question.type === 'multiple_choice' ? 'contains' : 'equals') && jump?.operator !== op))
                  .map((op) => (
                  <option key={op} value={op}>
                    {operatorLabel(op, locale)}
                  </option>
                ))}
              </select>
              {jump && !['answered', 'not_answered'].includes(jump.operator) && <ValueInput src={question} value={jump.value} onChange={(value) => onChange({ ...question, jumpIf: { ...jump, value } })} />}
              {jump && (
                <>
                  <span className="small">{t('logic.jump_to')}</span>
                  <select
                    className="select"
                    value={jumpTarget}
                    aria-label={t('logic.jump_target')}
                    onChange={(e) => {
                      const v = e.target.value;
                      onChange({ ...question, jumpIf: v.startsWith('end:') ? { ...jump, targetQuestionId: '', targetEndingId: v.slice(4) } : { ...jump, targetQuestionId: v, targetEndingId: undefined } });
                    }}
                  >
                    {idx < def.questions.length - 1 && (
                      <optgroup label={t('logic.jump_questions')}>
                        {def.questions.slice(idx + 1).map((target, n) => (
                          <option key={target.id} value={target.id}>
                            {idx + n + 2}. {short(target.title || t('common.untitled_question'))}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {endings.length > 0 && (
                      <optgroup label={t('logic.jump_endings')}>
                        {endings.map((e) => (
                          <option key={e.id} value={`end:${e.id}`}>
                            {t('logic.end_with', { name: e.name || e.id })}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </>
              )}
            </div>
            {jump?.targetEndingId && (
              <p className="hint row gap-8">
                <Flag size={14} /> {t('logic.ending_hint')}
              </p>
            )}
            {!endings.length && <p className="hint">{t('logic.endings_hint')}</p>}
          </div>
        </>
      )}
    </div>
  );
}
